/**
 * Device-local persistence for anonymous / unsaved build-guide progress.
 *
 * `startAutoSave` in builderStorePersistence only writes to Supabase, and only
 * when both `designId` and `userId` exist. A signed-out (or degraded-mode) user
 * who checks off checkpoints in the guide therefore loses everything on reload.
 * That silent data loss is worse than honest device-local storage, so this
 * module persists `buildProgress` for drafts that have no design id yet.
 *
 * Precedence: when a design row exists, Supabase remains the source of truth and
 * this store is never consulted. Local progress only backs unsaved drafts and is
 * merged (union, never destructive) into the design row on save/sign-in, after
 * which its key is cleared.
 *
 * Keyed on the stable seed identity of the draft (sauna type + seed key) so two
 * different unsaved drafts never contaminate each other's progress.
 */

import { normalizeBuildProgress } from "../domain/build/buildProgress";
import type { BuildProgress } from "../domain/build/getProjectBuildGuide";
import type { SaunaType } from "../types/design";

const KEY_PREFIX = "bb:anon-build-progress:";

export function hashString(value: string): string {
	// Small, stable, non-cryptographic hash — only needs to be collision-safe
	// enough to distinguish distinct seed keys, and short enough for a storage key.
	let hash = 0;
	for (let i = 0; i < value.length; i += 1) {
		hash = (hash * 31 + value.charCodeAt(i)) | 0;
	}
	return (hash >>> 0).toString(36);
}

/** Stable per-draft storage key. Derived from sauna type + seed identity. */
export function buildProgressLocalKey(
	type: SaunaType,
	seedKey: string,
): string {
	return `${KEY_PREFIX}${type}:${hashString(seedKey)}`;
}

export function getStorage(): Storage | null {
	try {
		if (typeof window === "undefined" || !window.localStorage) return null;
		return window.localStorage;
	} catch {
		// Access to localStorage can throw (private mode, disabled storage).
		return null;
	}
}

/**
 * Read and normalize device-local progress for a draft. Returns an empty map
 * when nothing is stored, storage is unavailable, or the payload is corrupt —
 * absent local progress must leave current behavior unchanged.
 */
export function readLocalBuildProgress(key: string): BuildProgress {
	const storage = getStorage();
	if (!storage) return {};

	try {
		const raw = storage.getItem(key);
		if (!raw) return {};
		return normalizeBuildProgress(JSON.parse(raw));
	} catch {
		return {};
	}
}

/**
 * Persist progress for a draft. An empty map clears the key rather than writing
 * `{}`, so a reset draft leaves no stale entry behind.
 */
export function writeLocalBuildProgress(
	key: string,
	progress: BuildProgress,
): void {
	const storage = getStorage();
	if (!storage) return;

	try {
		if (Object.keys(progress).length === 0) {
			storage.removeItem(key);
			return;
		}
		storage.setItem(key, JSON.stringify(progress));
	} catch {
		// Quota or serialization failures are non-fatal — progress simply
		// won't survive reload, which is the pre-existing behavior.
	}
}

/** Remove a draft's device-local progress (e.g. after a successful merge). */
export function clearLocalBuildProgress(key: string): void {
	const storage = getStorage();
	if (!storage) return;
	try {
		storage.removeItem(key);
	} catch {
		// no-op
	}
}
